/*
  NORI PDP — gallery + colourway swap

  Colourways are separate products. The mock swaps them in place rather than
  navigating, so the hero section serialises every sibling's data into a JSON
  blob at render time and this file repaints from it. No network call, so the
  swap is instant.

  Progressive enhancement: each swatch is a real <a href> to the sibling
  product, so with JS off (or before this file runs) clicking one simply
  navigates — the behaviour the theme has today.
*/
(function () {
  'use strict';

  // Gallery position labels come from the section, because the 0-4 contract
  // (front / annotated / interior / underside / back) belongs to a single bag.
  // A set's gallery is composite shots, so it supplies its own labels or none —
  // a wrong label is worse for alt text and SEO than no label at all.
  function labelsFor(root) {
    var raw = root.getAttribute('data-nori-gallery-labels') || '';
    return raw
      .split('|')
      .map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length; });
  }

  function altFor(colourway, index, labels) {
    var base = colourway.colour + ' ' + colourway.name;
    var label = labels[index];
    return label ? base + ' — ' + label : base;
  }

  function init(root) {
    var dataEl = root.querySelector('[data-nori-colourways]');
    if (!dataEl) return;

    var colourways;
    try {
      colourways = JSON.parse(dataEl.textContent);
    } catch (e) {
      // Malformed payload: leave the server-rendered markup alone rather than
      // blanking the gallery.
      return;
    }
    if (!Array.isArray(colourways) || !colourways.length) return;

    var stage = root.querySelector('[data-nori-stage]');
    var stageVideo = root.querySelector('[data-nori-stage-video]');
    var thumbsEl = root.querySelector('[data-nori-thumbs]');
    var swatchesEl = root.querySelector('[data-nori-swatches]');

    var labels = labelsFor(root);
    var current = colourways[0];
    var currentImage = 0;

    // The gallery list carries objects now ({src} or {src, video}); older
    // payloads were bare strings, so both shapes are accepted rather than
    // blanking a stage if a cached template and a fresh script ever meet.
    function entryOf(index) {
      var e = current.images[index];
      if (!e) return null;
      return typeof e === 'string' ? { src: e } : e;
    }

    function setImage(index) {
      var item = entryOf(index);
      if (!stage || !item) return;
      currentImage = index;

      if (item.video && stageVideo) {
        // A catalogue video: the poster stays as the <img> underneath while the
        // clip loads, so the stage never goes blank mid-swap.
        stage.src = item.src;
        stage.alt = altFor(current, index, labels);
        if (stageVideo.getAttribute('src') !== item.video) {
          stageVideo.setAttribute('src', item.video);
          stageVideo.load();
        }
        stageVideo.hidden = false;
        var played = stageVideo.play();
        if (played && played.catch) played.catch(function () {});
      } else {
        if (stageVideo) {
          stageVideo.pause();
          stageVideo.hidden = true;
        }
        stage.src = item.src;
        stage.alt = altFor(current, index, labels);
      }

      if (!thumbsEl) return;
      Array.prototype.forEach.call(thumbsEl.children, function (thumb, i) {
        thumb.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function buildThumbs() {
      if (!thumbsEl) return;
      thumbsEl.innerHTML = '';

      current.images.forEach(function (entry, i) {
        var item = typeof entry === 'string' ? { src: entry } : entry;
        var src = item.src;
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'np-thumb';
        button.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        button.setAttribute('aria-label', labels[i] || 'Product image ' + (i + 1));

        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';

        button.appendChild(img);
        if (item.video) {
          // A play mark, so a video thumb is not mistaken for a still.
          var tag = document.createElement('span');
          tag.className = 'np-thumb-play';
          tag.setAttribute('aria-hidden', 'true');
          button.appendChild(tag);
        }
        button.addEventListener('click', function () {
          setImage(i);
        });
        thumbsEl.appendChild(button);
      });
    }

    function paint() {
      var nameEl = root.querySelector('[data-nori-colour-name]');
      var priceEl = root.querySelector('[data-nori-price]');
      var mrpEl = root.querySelector('[data-nori-mrp]');
      var saveEl = root.querySelector('[data-nori-save]');
      var variantEl = root.querySelector('[data-nori-variant-id]');
      var atcEl = root.querySelector('[data-nori-atc]');

      if (nameEl) nameEl.textContent = current.colour;
      if (priceEl) priceEl.textContent = current.price;
      if (variantEl) variantEl.value = current.variant_id;

      // compare_at and the save badge are conditional — all three colourways
      // currently ship without a compare_at_price, so these must hide cleanly.
      if (mrpEl) {
        if (current.compare_at) {
          mrpEl.textContent = current.compare_at;
          mrpEl.hidden = false;
        } else {
          mrpEl.hidden = true;
        }
      }

      if (saveEl) {
        if (current.save_pct) {
          saveEl.textContent = current.save_pct + '% off';
          saveEl.hidden = false;
        } else {
          saveEl.hidden = true;
        }
      }

      if (atcEl) {
        atcEl.disabled = !current.available;
        // Three states, all set explicitly. Writing only the sold-out label
        // left it stuck there after switching back to a finish in stock, and a
        // pre-order finish has to say so rather than read "Add to cart".
        atcEl.textContent = !current.available
          ? atcEl.dataset.soldoutLabel || 'Sold out'
          : current.preorder
            ? atcEl.dataset.preorderLabel || 'Pre-order'
            : atcEl.dataset.atcLabel || 'Add to cart';

        // The _Pre-Order line-item property follows the colourway. A disabled
        // input is not submitted, which is how it is switched off.
        var preProp = root.querySelector('[data-nori-preorder-prop]');
        if (preProp) preProp.disabled = !current.preorder;
      }

      // Keep the sticky bar in step. It renders its own markup, so it listens
      // rather than being reached into from here.
      root.dispatchEvent(
        new CustomEvent('nori-pdp:colourway', { bubbles: true, detail: current })
      );
    }

    function select(colourway, shouldUpdateUrl) {
      current = colourway;
      buildThumbs();
      setImage(0);
      paint();

      if (swatchesEl) {
        Array.prototype.forEach.call(swatchesEl.children, function (sw) {
          sw.setAttribute(
            'aria-pressed',
            sw.dataset.handle === colourway.handle ? 'true' : 'false'
          );
        });
      }

      // Reflect the swap in the address bar so a refresh or a shared link
      // lands on the colourway the shopper is actually looking at.
      if (shouldUpdateUrl && window.history && window.history.replaceState) {
        var url = colourway.url + window.location.search;
        window.history.replaceState({}, '', url);
      }
    }

    function buildSwatches() {
      if (!swatchesEl) return;
      swatchesEl.innerHTML = '';

      colourways.forEach(function (colourway) {
        var link = document.createElement('a');
        link.className = 'np-sw';
        link.href = colourway.url;
        link.dataset.handle = colourway.handle;
        link.title = colourway.colour;
        link.setAttribute('role', 'button');
        link.setAttribute(
          'aria-pressed',
          colourway.handle === current.handle ? 'true' : 'false'
        );
        link.setAttribute('aria-label', colourway.colour);
        if (!colourway.available) link.dataset.soldOut = 'true';

        // Chip is the colourway's front image (spec). Gallery entries are
        // objects since the list started carrying video, so take .src — a bare
        // object here resolved to /products/[object Object] and 404'd.
        var chip = colourway.images[0];
        var img = document.createElement('img');
        img.src = typeof chip === 'string' ? chip : (chip && chip.src) || '';
        img.alt = '';
        img.loading = 'lazy';
        link.appendChild(img);

        // A colourway still on the old template carries swap: false. Its swatch
        // stays a plain link so the shopper lands on its own page, rather than
        // repainting this one with panels that belong to another finish.
        if (colourway.swap !== false) {
          link.addEventListener('click', function (event) {
            event.preventDefault();
            select(colourway, true);
          });
        }

        swatchesEl.appendChild(link);
      });
    }

    buildSwatches();
    buildThumbs();
    setImage(0);
    paint();

    // The colour carousel further down the page selects a colourway too.
    document.addEventListener('nori-pdp:select-colourway', function (event) {
      var match = colourways.filter(function (c) {
        return c.handle === event.detail;
      })[0];
      if (match && match.swap !== false) {
        select(match, true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (match) {
        // The colour carousel already suppressed its own navigation, so finish
        // the job here instead of swallowing the click.
        window.location.href = match.url;
      }
    });
  }

  function boot() {
    document.querySelectorAll('[data-nori-pdp-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
